/**
 * ActiveCallScreen — vrai appel client → agent vocal Cloudflare du pro ciblé.
 *
 * Levée de la simulation : l'audio part en WebSocket streaming continu vers le
 * DO `TenantVoiceAgent` (/agents/voice-agent/<room>) — le même pipeline que la
 * page pro /pro/agent-vocal (STT Flux → LLM llama-3.3 → TTS, barge-in inclus).
 *
 * Pont multi-tenant : au montage, POST /voice-agent/call/:tenantId (Worker)
 * minte un jeton court (KV, TTL 15 min) qui donne accès à la room de l'agent
 * du pro ciblé — le contexte (profil métier, connaissances, voix, RDV) est
 * celui du pro, jamais celui de l'appelant. Archivage + facturation sur le
 * tenant du pro, côté DO à la clôture de la connexion.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Typography,
  IconButton,
  Button,
  Dialog,
  Fade,
  Alert,
  Avatar,
} from '@mui/material';
import CallEndRoundedIcon from '@mui/icons-material/CallEndRounded';
import MicRoundedIcon from '@mui/icons-material/MicRounded';
import MicOffRoundedIcon from '@mui/icons-material/MicOffRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import ChatBubbleOutlineRoundedIcon from '@mui/icons-material/ChatBubbleOutlineRounded';
import VerifiedRoundedIcon from '@mui/icons-material/VerifiedRounded';
import { useVoiceAgent } from 'agents/voice/react';
import { useTranslation } from 'react-i18next';
import type { ProTenant } from '../../types';
import { env } from '../../config/env';
import { loadStoredToken } from '../../features/auth/model/session.slice';
import { LocalVadAudioInput } from '../../lib/voice/LocalVadAudioInput';
import { logger } from '../../lib/observability/logger';

export interface ActiveCallScreenProps {
  open: boolean;
  pro: ProTenant;
  /** Conservé pour l'interface (useCallLauncher) — nom affiché, données non utiles. */
  clientName?: string;
  onClose: () => void;
  /** Notifié au raccrochage (l'archivage serveur se fait côté DO). */
  onCallEnded?: () => void;
}

const STATUS_KEYS: Record<string, string> = {
  idle: 'call.connecting',
  listening: 'call.listening',
  thinking: 'call.thinking',
  speaking: 'call.speaking',
};

/** Retourne un message d'erreur actionnable si le micro est inutilisable, sinon null. */
async function checkMicrophone(): Promise<string | null> {
  if (!window.isSecureContext || !navigator.mediaDevices?.getUserMedia) {
    return 'Le micro n’est pas disponible : ouvrez NexaCraft en HTTPS.';
  }
  try {
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    stream.getTracks().forEach((track) => track.stop());
    return null;
  } catch (error) {
    const denied = error instanceof DOMException && (error.name === 'NotAllowedError' || error.name === 'SecurityError');
    return denied
      ? 'Autorisez l’accès au micro dans votre navigateur pour appeler l’agent.'
      : 'Aucun micro détecté. Branchez un micro puis réessayez.';
  }
}

export const ActiveCallScreen: React.FC<ActiveCallScreenProps> = ({
  open,
  pro,
  onClose,
  onCallEnded,
}) => {
  const [bridgeToken, setBridgeToken] = useState<string>('');
  const [bridgeError, setBridgeError] = useState<string | null>(null);
  const [showTranscript, setShowTranscript] = useState(false);
  const { t } = useTranslation();

  // Room unique par appel — une instance DO par conversation, le tenant visé
  // est porté par le jeton pont (résolu en KV côté serveur).
  const room = useMemo(
    () => `call-${Date.now().toString(36)}${Math.random().toString(36).slice(2, 8)}`,
    [],
  );
  const audioInput = useMemo(() => new LocalVadAudioInput(), []);

  // apiUrl peut être relatif (/api/v1) : résoudre avant d'en extraire l'hôte.
  const voiceHost = useMemo(
    () => new URL(env.apiUrl, window.location.origin).host,
    [],
  );

  const sessionToken = loadStoredToken();

  // Bridge : demande au Worker l'autorisation d'appeler l'agent de CE pro.
  useEffect(() => {
    let cancelled = false;
    setBridgeToken('');
    setBridgeError(null);
    if (!open) return () => { cancelled = true; };
    if (!sessionToken) {
      setBridgeError(t('call.sessionRequired'));
      return undefined;
    }
    void (async () => {
      try {
        // Micro vérifié avant le pont : un refus ou un contexte non sécurisé doit être dit clairement,
        // pas découvert après une attente silencieuse.
        const micError = await checkMicrophone();
        if (micError) throw new Error(micError);
        const res = await fetch(`${env.apiUrl}/voice-agent/call/${pro.id}`, {
          method: 'POST',
          credentials: 'include',
          headers: sessionToken ? { Authorization: `Bearer ${sessionToken}` } : undefined,
        });
        const body = (await res.json().catch(() => null)) as
          | { data?: { token?: string }; error?: { message?: string } }
          | null;
        if (cancelled) return;
        if (!res.ok || !body?.data?.token) {
          throw new Error(body?.error?.message ?? 'Service vocal indisponible.');
        }
        setBridgeToken(body.data.token);
      } catch (e) {
        if (!cancelled) {
          setBridgeError(String(e instanceof Error ? e.message : e));
          logger.warn('voice.bridge.failed', { tenantId: pro.id, error: String(e) });
        }
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, pro.id]);

  const {
    status,
    transcript,
    interimTranscript,
    isMuted,
    connected,
    error,
    startCall,
    endCall,
    toggleMute,
  } = useVoiceAgent({
    agent: 'VoiceAgent',
    name: room,
    host: voiceHost,
    query: { token: bridgeToken },
    enabled: Boolean(bridgeToken),
    audioInput,
    // Seuils au-dessus du bruit ambiant (TV, écho de pièce) : le barge-in
    // doit couper sur la vraie parole, pas sur un bruit. Le VAD envoie les
    // niveaux par fenêtres de ~21 ms : interruptChunks: 15 exige ~300 ms de
    // parole continue — en dessous, l'écho du haut-parleur ou un claquement
    // coupaient la phrase de l'agent en plein milieu (vu en E2E).
    silenceThreshold: 0.025,
    silenceDurationMs: 450,
    interruptThreshold: 0.03,
    interruptChunks: 15,
  });

  // Démarre l'appel quand le pont est en main ET la WebSocket ouverte :
  // startCall() échoue silencieusement tant que transport n'est pas connecté
  // (connexion de 1-3 s), ce qui laissait l'appel bloqué sur « Connexion… ».
  // Une seule tentative ; en cours d'appel le client renvoie `start_call`
  // lui-même à chaque reconnexion.
  const startIssuedRef = useRef(false);
  useEffect(() => {
    if (bridgeToken && connected && status === 'idle' && !startIssuedRef.current) {
      startIssuedRef.current = true;
      void startCall();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bridgeToken, connected]);

  // Chronomètre local (facturation : côté serveur, DO).
  const [duration, setDuration] = useState(0);
  const inCall = status !== 'idle';
  useEffect(() => {
    if (!inCall) return undefined;
    const timer = setInterval(() => setDuration((d) => d + 1), 1000);
    return () => clearInterval(timer);
  }, [inCall]);

  const handleHangUp = () => {
    void endCall();
    onCallEnded?.();
    setTimeout(onClose, 600);
  };

  const formatTime = (secs: number) => {
    const m = Math.floor(secs / 60).toString().padStart(2, '0');
    const s = (secs % 60).toString().padStart(2, '0');
    return `${m}:${s}`;
  };

  if (!open) return null;

  return (
    <Dialog
      open={open}
      fullScreen
      onClose={handleHangUp}
      slotProps={{
        paper: {
          sx: {
            bgcolor: '#0D1326',
            color: '#FFFFFF',
            p: 0,
            m: 0,
            overflow: 'hidden',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'radial-gradient(ellipse 80% 80% at 50% -20%, #1A2850 0%, #0D1326 60%, #080D1D 100%)',
          },
        },
      }}
    >
      <Box
        sx={{
          width: '100%',
          maxWidth: { xs: '100%', md: 820 },
          height: { xs: '100%', md: 'auto' },
          minHeight: { xs: '100%', md: 540 },
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
          bgcolor: { xs: '#0D1326', md: 'rgba(16, 25, 53, 0.85)' },
          backdropFilter: { xs: 'none', md: 'blur(30px)' },
          borderRadius: { xs: 0, md: '32px' },
          border: { xs: 'none', md: '1px solid rgba(255, 255, 255, 0.12)' },
          overflow: 'hidden',
          p: { xs: 3, sm: 4, md: 4.5 },
        }}
      >
        {/* Identité du professionnel + durée */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Avatar
              src={pro.avatar}
              alt={pro.name}
              sx={{
                width: { xs: 48, md: 56 },
                height: { xs: 48, md: 56 },
                borderRadius: '16px',
                border: '2px solid #FF3567',
                boxShadow: '0 4px 16px rgba(255, 53, 103, 0.35)',
              }}
            />
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                <Typography variant="h6" sx={{ fontWeight: 800, fontSize: { xs: '1.2rem', md: '1.4rem' }, color: '#FFFFFF', letterSpacing: '-0.02em' }}>
                  {pro.name}
                </Typography>
                {pro.verified && <VerifiedRoundedIcon sx={{ fontSize: 18, color: '#3B82F6' }} />}
              </Box>
              <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.65)', fontSize: '0.84rem', fontWeight: 600 }}>
                {pro.trade}
              </Typography>
            </Box>
          </Box>
          <Typography
            variant="subtitle1"
            sx={{ color: '#FFFFFF', fontWeight: 800, fontVariantNumeric: 'tabular-nums', letterSpacing: '0.05em' }}
          >
            {connected ? formatTime(duration) : '00:00'}
          </Typography>
        </Box>

        {/* Erreurs : session manquante, pont refusé, WebSocket en échec */}
        {(bridgeError || error) && (
          <Alert
            severity="error"
            sx={{ mt: 2, borderRadius: '12px', bgcolor: 'rgba(239,68,68,0.12)', zIndex: 2 }}
          >
            {bridgeError ?? error}
          </Alert>
        )}

        {/* Orbe vocal : état réel du pipeline (parole / réflexion / écoute) */}
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 3,
          }}
        >
          <Box
            sx={{
              position: 'relative',
              width: { xs: 200, md: 240 },
              height: { xs: 200, md: 240 },
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Box
              sx={{
                position: 'absolute',
                inset: 0,
                borderRadius: '50%',
                background: 'radial-gradient(circle at 35% 35%, #FF3567 0%, #7C3AED 50%, transparent 80%)',
                filter: 'blur(45px)',
                opacity: status === 'speaking' ? 0.9 : status === 'thinking' ? 0.7 : 0.4,
                transform: status === 'speaking' ? 'scale(1.12)' : 'scale(1)',
                transition: 'all 0.35s ease',
              }}
            />
            <Box
              sx={{
                position: 'absolute',
                width: '80%',
                height: '80%',
                borderRadius: '50%',
                background: 'radial-gradient(circle at 65% 65%, #3B82F6 0%, transparent 75%)',
                filter: 'blur(40px)',
                opacity: status === 'listening' ? 0.85 : 0.3,
                transform: status === 'listening' ? 'scale(1.15)' : 'scale(1)',
                transition: 'all 0.35s ease',
                '@keyframes pulse': {
                  '0%': { transform: 'scale(0.95)', opacity: 0.65 },
                  '50%': { transform: 'scale(1.1)', opacity: 0.95 },
                  '100%': { transform: 'scale(0.95)', opacity: 0.65 },
                },
                animation: inCall ? 'pulse 1.2s infinite' : 'none',
              }}
            />
            <Box
              sx={{
                position: 'relative',
                zIndex: 2,
                bgcolor: 'rgba(13, 19, 38, 0.75)',
                backdropFilter: 'blur(20px)',
                px: 2.5,
                py: 1,
                borderRadius: '24px',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                boxShadow: '0 8px 24px rgba(0,0,0,0.4)',
              }}
            >
              <Typography variant="body2" sx={{ color: '#FFFFFF', fontWeight: 700, fontSize: '0.85rem' }}>
                {inCall ? t(STATUS_KEYS[status] ?? 'call.connecting') : t('call.connecting')}
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* Contrôles : micro (réel) + transcription + raccrocher */}
        <Box sx={{ zIndex: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: { xs: 3, md: 5 }, mb: { xs: 3, md: 4 } }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <IconButton
                onClick={toggleMute}
                disabled={!inCall}
                aria-label={isMuted ? t('call.unmute') : t('call.muteOn')}
                sx={{
                  width: { xs: 54, md: 62 },
                  height: { xs: 54, md: 62 },
                  bgcolor: isMuted ? '#FFFFFF' : 'rgba(255, 255, 255, 0.12)',
                  color: isMuted ? '#0D1326' : '#FFFFFF',
                  borderRadius: '50%',
                  border: '1px solid rgba(255, 255, 255, 0.15)',
                  transition: 'all 0.2s ease',
                  '&:hover': { bgcolor: isMuted ? '#F8FAFC' : 'rgba(255, 255, 255, 0.22)' },
                }}
              >
                {isMuted ? <MicOffRoundedIcon sx={{ fontSize: 24 }} /> : <MicRoundedIcon sx={{ fontSize: 24 }} />}
              </IconButton>
              <Typography variant="caption" sx={{ mt: 0.75, color: 'rgba(255, 255, 255, 0.8)', fontSize: '0.75rem', fontWeight: 600 }}>
                {isMuted ? t('call.unmute') : t('call.mute')}
              </Typography>
            </Box>

            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <IconButton
                onClick={() => setShowTranscript(true)}
                disabled={!inCall}
                aria-label="Transcription"
                sx={{
                  width: { xs: 54, md: 62 },
                  height: { xs: 54, md: 62 },
                  bgcolor: 'rgba(255, 255, 255, 0.12)',
                  color: '#FFFFFF',
                  borderRadius: '50%',
                  border: '1px solid rgba(255, 255, 255, 0.15)',
                  transition: 'all 0.2s ease',
                  '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.22)' },
                }}
              >
                <ChatBubbleOutlineRoundedIcon sx={{ fontSize: 22 }} />
              </IconButton>
              <Typography variant="caption" sx={{ mt: 0.75, color: 'rgba(255, 255, 255, 0.8)', fontSize: '0.75rem', fontWeight: 600 }}>                  {t('call.transcript')}
                </Typography>
            </Box>
          </Box>

          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
            <Button
              variant="contained"
              onClick={handleHangUp}
              startIcon={<CallEndRoundedIcon sx={{ fontSize: 26 }} />}
              sx={{
                bgcolor: '#FF3567',
                color: '#FFFFFF',
                borderRadius: '30px',
                px: { xs: 4, sm: 5 },
                py: 1.5,
                fontWeight: 800,
                fontSize: '1rem',
                textTransform: 'none',
                boxShadow: '0 8px 24px rgba(255, 53, 103, 0.45)',
                transition: 'all 0.2s ease',
                '&:hover': { bgcolor: '#E62053' },
              }}
            >                {t('call.hangup')}
              </Button>
          </Box>
        </Box>

        {/* Overlay transcription temps réel (données du DO) */}
        {showTranscript && (
          <Fade in={showTranscript}>
            <Box
              sx={{
                position: 'absolute',
                inset: 0,
                bgcolor: '#0D1326',
                backdropFilter: 'blur(20px)',
                zIndex: 20,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
                p: { xs: 3, md: 4 },
              }}
            >
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6" sx={{ fontWeight: 800, color: '#FFFFFF' }}>
                  {t('call.transcriptTitle')}
                </Typography>
                <IconButton onClick={() => setShowTranscript(false)} sx={{ color: '#FFFFFF' }}>
                  <CloseRoundedIcon />
                </IconButton>
              </Box>

              <Box sx={{ flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 1.5, pr: 1 }}>
                {transcript.length === 0 && !interimTranscript && (
                  <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.6)' }}>
                    {t('call.emptyTranscript')}
                  </Typography>
                )}
                {transcript.map((msg, i) => (
                  <Box
                    key={i}
                    sx={{
                      alignSelf: msg.role === 'user' ? 'flex-end' : 'flex-start',
                      maxWidth: '85%',
                      p: 1.5,
                      borderRadius: '16px',
                      bgcolor: msg.role === 'user' ? '#FF3567' : 'rgba(255, 255, 255, 0.12)',
                      color: '#FFFFFF',
                    }}
                  >
                    <Typography variant="caption" sx={{ opacity: 0.7, display: 'block', mb: 0.25 }}>
                      {msg.role === 'user' ? t('call.you') : pro.name}
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {msg.text}
                    </Typography>
                  </Box>
                ))}
                {interimTranscript && (
                  <Box
                    sx={{
                      alignSelf: 'flex-end',
                      maxWidth: '85%',
                      p: 1.5,
                      borderRadius: '16px',
                      border: '1px dashed rgba(255,255,255,0.4)',
                    }}
                  >
                    <Typography variant="body2" sx={{ fontStyle: 'italic', color: 'rgba(255,255,255,0.85)' }}>
                      {interimTranscript}
                    </Typography>
                  </Box>
                )}
              </Box>

              <Button
                fullWidth
                variant="outlined"
                onClick={() => setShowTranscript(false)}
                sx={{ borderRadius: '24px', color: '#FFFFFF', borderColor: 'rgba(255,255,255,0.3)', mt: 2 }}
              >
                {t('call.close')}
              </Button>
            </Box>
          </Fade>
        )}
      </Box>
    </Dialog>
  );
};
